/**
 * Footer — Iter 67ai redesign.
 *
 * Replaces the previous 4-column "Explore / Account" minimal footer with a
 * richer dark-gradient footer that doubles as a final social-proof + CTA
 * surface. Three tiers:
 *
 *   1. CTA strip — "List your car" + "Browse cars" pair of high-contrast
 *      pills, sitting on a neutral-900 panel that bleeds the page out.
 *   2. Link grid — Brand column (logo + tagline + social icons), Explore,
 *      Hosts, Renters, Company. Wider grid with proper breathing room.
 *   3. Bottom bar — copyright, currency/lang badges, payment-method icons,
 *      and a "Made with care in Tirana 🇦🇱" line that nods to the founding
 *      country.
 *
 * Social icons are inline SVG (no extra deps) and link to platform-level
 * handles — vendor-specific socials still show on the car/policies pages.
 * Replace the placeholder URLs with real handles once they're live.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Car, Mail, MapPin, ShieldCheck, Star, Sparkles } from "lucide-react";

const SOCIAL_NETWORKS = [
  { key: "social_instagram", label: "Instagram", icon: InstagramGlyph },
  { key: "social_facebook",  label: "Facebook",  icon: FacebookGlyph },
  { key: "social_x",         label: "X",         icon: XGlyph },
  { key: "social_tiktok",    label: "TikTok",    icon: TikTokGlyph },
  { key: "social_linkedin",  label: "LinkedIn",  icon: LinkedInGlyph },
];

export default function Footer() {
  const { t } = useTranslation();
  // Iter 67aj — admin-managed social URLs. Endpoint always returns a fully-shaped
  // payload, so the "show this icon if URL is non-empty" filter Just Works™.
  const [socials, setSocials] = useState({});
  useEffect(() => {
    api.get("/site-settings")
      .then((r) => setSocials(r.data || {}))
      .catch(() => setSocials({}));
  }, []);
  const visibleSocials = SOCIAL_NETWORKS
    .map((n) => ({ ...n, href: (socials[n.key] || "").trim() }))
    .filter((n) => n.href);

  return (
    <footer className="mt-24" data-testid="footer">
      {/* ----- 1. Final CTA ribbon ----- */}
      <section className="bg-gradient-to-br from-neutral-950 via-indigo-950 to-blue-950 text-white relative overflow-hidden" data-testid="footer-cta">
        <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_30%_20%,white_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-amber-300 text-[10px] font-bold uppercase tracking-[0.18em] mb-4 border border-white/10 backdrop-blur-sm">
                <Sparkles className="h-3 w-3" /> Ready to drive?
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold leading-[1.05]">
                Start your trip,<br />
                <span className="text-white/60">or list your car.</span>
              </h2>
              <p className="text-sm text-white/60 mt-5 max-w-md leading-relaxed">
                Join thousands of renters and trusted local hosts in Albania &amp; across the Balkans — book in minutes, drive in confidence.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-testid="footer-cta-buttons">
              <Link
                to="/cars"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-neutral-900 font-semibold text-sm hover:bg-neutral-200 transition shadow-xl"
                data-testid="footer-cta-browse"
              >
                Browse cars <Car className="h-4 w-4" />
              </Link>
              <Link
                to="/become-vendor"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-amber-950 font-semibold text-sm transition shadow-xl shadow-amber-500/20"
                data-testid="footer-cta-host"
              >
                List your car <Sparkles className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ----- 2. Link grid ----- */}
      <div className="bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-12 gap-y-10 gap-x-8">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-4 space-y-5">
            <Link to="/" className="inline-flex items-center gap-2 group" data-testid="footer-brand-link">
              {/* Iter 67en+++ — Use the same amber "R" mark as the header so
                  brand recognition stays consistent across the page. The
                  amber square also pops nicely against the dark footer. */}
              <img
                src="/logo-192.png"
                alt="RentalCar.al"
                className="h-10 w-10 object-contain rounded-2xl group-hover:scale-105 transition [filter:drop-shadow(0_0_14px_rgba(251,191,36,0.35))] group-hover:[filter:drop-shadow(0_0_20px_rgba(251,191,36,0.55))]"
                width="40"
                height="40"
              />
              <div className="font-display text-2xl font-semibold tracking-tight">
                RentalCar<span className="text-red-600">.al</span>
              </div>
            </Link>
            <p className="text-sm text-white/60 max-w-xs leading-relaxed">
              A multi-vendor marketplace for renting cars from trusted local hosts across Albania — Tirana, Sarandë, Berat, Durrës, Shkodër and beyond.
            </p>
            {/* Social row — only renders the icons the admin has supplied URLs
                for. Hidden entirely when none are set so the layout doesn't sit
                on a row of "dead" buttons. */}
            {visibleSocials.length > 0 && (
              <div className="flex items-center gap-2.5" data-testid="footer-socials">
                {visibleSocials.map(({ label, href, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 hover:border-white/30 flex items-center justify-center transition group/social"
                    aria-label={label}
                    title={label}
                    data-testid={`footer-social-${label.toLowerCase()}`}
                  >
                    <Icon className="h-4 w-4 text-white/80 group-hover/social:text-white transition" />
                  </a>
                ))}
              </div>
            )}
            {/* Trust pills */}
            <div className="flex flex-wrap gap-2 pt-2" data-testid="footer-trust-pills">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[11px] font-medium">
                <ShieldCheck className="h-3 w-3" /> Insured rentals
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20 text-[11px] font-medium">
                <Star className="h-3 w-3" /> Verified hosts
              </span>
            </div>
          </div>

          {/* Explore — Iter 69: Albanian cities (matches Browse city chips).
              Tirana Airport routes to its content-rich SEO landing page
              instead of `/cars?city=…` because the underlying car location
              labels are "Tirana" — the airport landing already lists those
              cars with map + FAQs + meet-greet info. */}
          <FooterColumn label="Explore" testId="footer-col-explore">
            <FooterLink to="/cars">All cars</FooterLink>
            <FooterLink to="/car-rental/tirana-airport-albania">Tirana Airport</FooterLink>
            <FooterLink to="/cars?city=Saranda">Sarande</FooterLink>
            <FooterLink to="/cars?city=Durrës">Durrës</FooterLink>
            <FooterLink to="/cars?city=Vlora">Vlorë</FooterLink>
          </FooterColumn>

          {/* For hosts */}
          <FooterColumn label="For hosts" testId="footer-col-hosts">
            <FooterLink to="/become-vendor">List your car</FooterLink>
            <FooterLink to="/dashboard/vendor">Vendor dashboard</FooterLink>
            <FooterLink to="/widget-builder">Embed search widget</FooterLink>
            <FooterLink to="/contact">Vendor support</FooterLink>
          </FooterColumn>

          {/* For renters */}
          <FooterColumn label="For renters" testId="footer-col-renters">
            <FooterLink to="/login">Log in</FooterLink>
            <FooterLink to="/register">Sign up</FooterLink>
            <FooterLink to="/dashboard/customer">My bookings</FooterLink>
            <FooterLink to="/faq">FAQ</FooterLink>
            <FooterLink to="/contact">Help & support</FooterLink>
          </FooterColumn>

          {/* Company */}
          <FooterColumn label="Company" testId="footer-col-company">
            <FooterLink to="/contact">Contact us</FooterLink>
            <a href="mailto:hello@rentalcar.al" className="text-sm text-white/70 hover:text-white transition flex items-center gap-1.5" data-testid="footer-link-email">
              <Mail className="h-3 w-3" /> hello@rentalcar.al
            </a>
            <span className="text-sm text-white/70 flex items-center gap-1.5" data-testid="footer-link-hq">
              <MapPin className="h-3 w-3" /> Tirana, Albania
            </span>
          </FooterColumn>
        </div>

        {/* ----- 3. Bottom bar ----- */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/50">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span data-testid="footer-copyright">© {new Date().getFullYear()} RentalCar.al. All rights reserved.</span>
              <span className="hidden sm:inline opacity-50">·</span>
              <Link to="/privacy" className="hover:text-white transition" data-testid="footer-link-privacy">{t("footer.legal.privacy", "Privacy")}</Link>
              <span className="opacity-50">·</span>
              <Link to="/terms" className="hover:text-white transition" data-testid="footer-link-terms">{t("footer.legal.terms", "Terms")}</Link>
              <span className="opacity-50">·</span>
              <Link to="/about" className="hover:text-white transition" data-testid="footer-link-about">{t("footer.legal.about", "About")}</Link>
              <span className="opacity-50">·</span>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("rentalcar:open-cookie-banner"))}
                className="hover:text-white transition"
                data-testid="footer-link-cookies"
              >
                {t("footer.legal.cookies", "Cookies")}
              </button>
            </div>
            <div className="text-[11px] text-white/40" data-testid="footer-tagline">
              Made with care in Tirana, Albania
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ label, children, testId }) {
  return (
    <div className="md:col-span-2" data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.18em] text-white/40 font-bold mb-4">{label}</div>
      <ul className="space-y-2.5">
        {Array.isArray(children) ? children.map((c, i) => <li key={i}>{c}</li>) : <li>{children}</li>}
      </ul>
    </div>
  );
}

function FooterLink({ to, children }) {
  // Iter 69 — clicking a footer link must always land the renter at the top
  // of the new page, including the case where only the query string changes
  // (e.g. /cars → /cars?city=Saranda). The global <ScrollToTop> ignores
  // query-only updates so filters elsewhere don't snap; here we opt-in.
  const handleClick = () => {
    if (typeof window === "undefined") return;
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
  };
  return (
    <Link
      to={to}
      onClick={handleClick}
      className="text-sm text-white/70 hover:text-white transition"
    >
      {children}
    </Link>
  );
}

/* ────────────── Inline social-network glyphs ────────────── */

function InstagramGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
    </svg>
  );
}

function FacebookGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.3-1.5 1.6-1.5h1.7V4.6c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.1v2.3H7.7V14h2.7v8h3.1z" />
    </svg>
  );
}

function XGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M17.7 3h3.3l-7.2 8.2L22 21h-6.6l-5.2-6.7L4.3 21H1l7.7-8.8L1.4 3h6.7l4.7 6.1L17.7 3zm-1.2 16h1.8L7.6 4.9H5.7L16.5 19z" />
    </svg>
  );
}

function TikTokGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M19.6 8.3a6.7 6.7 0 0 1-3.9-1.2v7.7a5.4 5.4 0 1 1-5.4-5.4c.3 0 .6 0 .8.1v2.7a2.7 2.7 0 1 0 1.9 2.6V3h2.7a4 4 0 0 0 4 3.6v1.7z"/>
    </svg>
  );
}

function LinkedInGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.22 8h4.56v14H.22V8zm7.4 0h4.37v1.92h.06c.61-1.15 2.1-2.36 4.32-2.36 4.62 0 5.47 3.04 5.47 6.99V22h-4.55v-6.18c0-1.47-.03-3.36-2.05-3.36-2.05 0-2.36 1.6-2.36 3.25V22H7.62V8z"/>
    </svg>
  );
}
